import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';

import { getAdminNotifications } from '@store/notification/notification.actions';

import { environment } from '@env/environment';
import { EditMapDeactivateModalService } from '@core/auth/edit-map-deactivate-modal.service';
import { RedirectService } from '@core/helper/redirect.service';
import { AuthMode } from '@feature/auth/auth.component';
import * as fromApp from '@store';
import { logout } from '@store/auth/auth.actions';
import { AuthState } from '@store/auth/auth.reducer';
import { selectActAsUser, selectAuthState, selectCurrentUser } from '@store/auth/auth.selectors';
import { selectNotificationState } from '@store/notification/notification.selectors';
import { RoleEnum } from '../constants/role.enums';
import { Site, User } from '../models';
import { AppConfigService } from '@core/api/app-config.service';
import { StandSelectionStore } from '@shared-map/components/map-layers-pages/stand-selection.store';

@Component({
  selector: 'fc-app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  standalone: false
})
export class AppHeaderComponent implements OnInit {
  currentMenuOpen: string = '';
  actAsUser: User = null;
  activeSite: Site;
  adminFailedVerificationUsersCount: number = 0;
  authModeEnum = AuthMode;
  authState: AuthState = null;
  currentUser: User = null;
  env = environment.env;
  infoMessage = '';
  isAdmin: boolean = false;
  isReadAdmin: boolean = false;
  isSuperAdmin: boolean = false;
  landOwnerAccountNotificationBadgeCount = 0;
  loggedIn = false;
  roles = RoleEnum;
  showInfoMessage = false;

  destroyRef = inject(DestroyRef);

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly editMapDeactivateModalService: EditMapDeactivateModalService,
    readonly standSelectService: StandSelectionStore,
    readonly redirectService: RedirectService,
    readonly appConfigService: AppConfigService
  ) {}

  ngOnInit() {
    this.currentMenuOpen = '';
    this.infoMessage = this.appConfigService.getValue('banner_message', '');
    this.showInfoMessage = this.infoMessage.trim() !== '';
    this.store
      .select(selectAuthState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(authState => {
        this.loggedIn = !isEmpty(authState.token);
        this.authState = authState;
      });

    this.store
      .select(selectNotificationState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(notificationState => {
        this.landOwnerAccountNotificationBadgeCount = 0;
        if (
          this.currentUser &&
          notificationState.landOwner.userId &&
          this.currentUser.id === notificationState.landOwner.userId
        ) {
          this.landOwnerAccountNotificationBadgeCount += notificationState.landOwner.paymentsAccountRequiresSetup;
          if (notificationState.landOwner.stripeAccountRequiresAction) {
            this.landOwnerAccountNotificationBadgeCount++;
          }
        }

        this.adminFailedVerificationUsersCount = notificationState.admin.failedVerificationUsersCount;
      });

    this.store
      .select(selectCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.currentUser = currentUser;
        this.isSuperAdmin = currentUser?.isSuperAdmin;
        this.isAdmin = currentUser?.isWriteAdmin;
        this.isReadAdmin = currentUser?.isReadOnlyAdmin;
        if (this.isAdmin) {
          this.store.dispatch(getAdminNotifications());
        }
      });

    this.store
      .select(selectActAsUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(actAsUser => {
        if (actAsUser) {
          this.actAsUser = new User(actAsUser);
        } else {
          this.actAsUser = null;
        }
      });
  }

  logout(): void {
    this.currentMenuOpen = '';
    if (this.editMapDeactivateModalService.dirty) {
      this.editMapDeactivateModalService.openForLogout();
    } else {
      this.store.dispatch(logout());
    }
  }

  menuOpened(menu: string) {
    this.currentMenuOpen = menu;
  }

  menuClosed() {
    this.currentMenuOpen = '';
  }

  navigate(): void {
    this.redirectService.redirectToDashboard();
  }

  get isVerified(): boolean {
    return !this.currentUser || this.currentUser.isVerified;
  }

  closeInfoMessage() {
    this.showInfoMessage = false;
  }

  goToLogin() {
    this.router.navigate(['auth']);
  }

  goToHelp() {
    window.open('https://landyield.com/faq', '_blank', 'noopener noreferrer');
  }

  isManageInfoActive(): boolean {
    return this.router.url.startsWith('/admin/manage');
  }
}
